import React, { useContext, useEffect, useMemo, useState } from "react";
import { Badge, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row, Table, Button } from "reactstrap";
import Header from "../../components/Headers/Header";
import AdminNavbar from "../../components/Navbars/AdminNavbar/AdminNavbar";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import { AuthContext } from "../../context/AuthContext";
import useVendasDashboard from "../../context/hooks/useVendasDashboard";
import api from "../../api";
import { useToast } from "../../context/ToastContext";

function formatCurrency(value: number): string {
    return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(value: string | null): string {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("pt-BR");
}

function getStartOfMonthISO(): string {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split("T")[0];
}

function getTodayISO(): string {
    return new Date().toISOString().split("T")[0];
}

interface StatCardProps {
    label: string;
    value: string | number;
    icon: string;
    color: string;
    subtitle?: string;
}

function StatCard({ label, value, icon, color, subtitle }: StatCardProps): React.ReactElement {
    return (
        <Card className="shadow border-0 h-100">
            <CardBody>
                <Row className="align-items-center">
                    <Col className="col-auto">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: 48, height: 48, backgroundColor: color }}
                        >
                            <i className={`${icon} text-white`} style={{ fontSize: 18 }} />
                        </div>
                    </Col>
                    <Col>
                        <div className="text-uppercase text-muted" style={{ fontSize: 11, fontWeight: 700 }}>
                            {label}
                        </div>
                        <h3 className="mb-0">{value}</h3>
                        {subtitle && <small className="text-muted d-block mt-1">{subtitle}</small>}
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
}

const VendasDashboard: React.FC = () => {
    const showToast = useToast();
    const { objUserTk } = useContext(AuthContext);
    const { resumo, loading, fetchResumo } = useVendasDashboard();
    const [metaLoading, setMetaLoading] = useState(false);
    const [metas, setMetas] = useState<any[]>([]);
    const [vendedores, setVendedores] = useState<{ id: number; name: string }[]>([]);
    const [metaForm, setMetaForm] = useState({
        id: 0,
        vendedorId: 0,
        periodoInicio: getStartOfMonthISO(),
        periodoFim: getTodayISO(),
        metaValor: "",
        metaQuantidade: "",
        metaComissao: "",
        ativa: true,
    });

    const [filters, setFilters] = useState({
        dataInicial: getStartOfMonthISO(),
        dataFinal: getTodayISO(),
        vendedorId: 0,
    });

    const empresaId = useMemo(() => objUserTk.EmpresaId, [objUserTk]);

    useEffect(() => {
        if (!empresaId) return;
        fetchResumo({
            empresaId,
            dataInicial: filters.dataInicial || null,
            dataFinal: filters.dataFinal || null,
            vendedorId: filters.vendedorId || null,
        });
    }, [empresaId]);

    useEffect(() => {
        if (!empresaId) return;
        loadMetas();
        loadVendedores();
    }, [empresaId]);

    const loadMetas = async () => {
        setMetaLoading(true);
        try {
            const { data } = await api.get(`/MetaVendedor?empresaId=${empresaId}`);
            setMetas(data || []);
        } catch {
            showToast("error", "Erro ao carregar metas.");
        } finally {
            setMetaLoading(false);
        }
    };

    const loadVendedores = async () => {
        try {
            const response = await api.get("/Terceiro/getTerceiroPaged", {
                params: {
                    pageNumber: 1,
                    pageSize: 1000,
                    nome: "",
                    empresaId,
                    tipoTerceiro: "Vendedor",
                },
            });
            setVendedores(response.data?.result || []);
        } catch {
            showToast("error", "Erro ao carregar vendedoras.");
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: name === "vendedorId" ? Number(value) : value,
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchResumo({
            empresaId,
            dataInicial: filters.dataInicial || null,
            dataFinal: filters.dataFinal || null,
            vendedorId: filters.vendedorId || null,
        });
    };

    const handleClear = () => {
        const reset = {
            dataInicial: getStartOfMonthISO(),
            dataFinal: getTodayISO(),
            vendedorId: 0,
        };
        setFilters(reset);
        fetchResumo({
            empresaId,
            dataInicial: reset.dataInicial,
            dataFinal: reset.dataFinal,
            vendedorId: null,
        });
    };

    const handleMetaChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setMetaForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
        }));
    };

    const handleSaveMeta = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post("/MetaVendedor", {
                id: metaForm.id || null,
                empresaId,
                vendedorId: metaForm.vendedorId,
                periodoInicio: metaForm.periodoInicio,
                periodoFim: metaForm.periodoFim,
                metaValor: Number(metaForm.metaValor),
                metaQuantidade: metaForm.metaQuantidade ? Number(metaForm.metaQuantidade) : null,
                metaComissao: metaForm.metaComissao ? Number(metaForm.metaComissao) : null,
                ativa: metaForm.ativa,
            });
            showToast("success", "Meta salva com sucesso.");
            await loadMetas();
            await fetchResumo({
                empresaId,
                dataInicial: filters.dataInicial || null,
                dataFinal: filters.dataFinal || null,
                vendedorId: filters.vendedorId || null,
            });
            setMetaForm({
                id: 0,
                vendedorId: 0,
                periodoInicio: getStartOfMonthISO(),
                periodoFim: getTodayISO(),
                metaValor: "",
                metaQuantidade: "",
                metaComissao: "",
                ativa: true,
            });
        } catch {
            showToast("error", "Erro ao salvar meta.");
        }
    };

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText="Dashboard de Vendas" search={false} titleSearch="" />

            <Container className="mt-4" fluid>
                <div className="mb-4">
                    <h4 className="mb-1">Dashboard de Vendas</h4>
                    <p className="text-muted mb-0">Resumo de performance por vendedora com comissão mockada e filtros por período.</p>
                </div>

                <Card className="shadow border-0 mb-4">
                    <CardBody>
                        <Form onSubmit={handleSubmit}>
                            <Row form>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Data inicial</Label>
                                        <Input type="date" name="dataInicial" value={filters.dataInicial} onChange={handleChange} />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Data final</Label>
                                        <Input type="date" name="dataFinal" value={filters.dataFinal} onChange={handleChange} />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label>Vendedora</Label>
                                        <Input type="select" name="vendedorId" value={filters.vendedorId} onChange={handleChange}>
                                            <option value={0}>Todas</option>
                                            {(resumo?.vendedoras ?? []).map(v => (
                                                <option key={v.vendedorId} value={v.vendedorId}>
                                                    {v.vendedorNome}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md="2" className="d-flex align-items-end">
                                    <div className="w-100">
                                        <Button color="primary" type="submit" block>
                                            Filtrar
                                        </Button>
                                        <Button color="secondary" type="button" block className="mt-2" onClick={handleClear}>
                                            Limpar
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </CardBody>
                </Card>

                {loading || !resumo ? (
                    <Row className="justify-content-center mt-5">
                        <Col className="text-center text-muted">Carregando dados...</Col>
                    </Row>
                ) : (
                    <>
                        <Row className="g-3 mb-4">
                            <Col xl="3" lg="6" md="6">
                                <StatCard
                                    label="Total de vendas"
                                    value={resumo.totalVendas}
                                    icon="fa fa-shopping-cart"
                                    color="#5e72e4"
                                    subtitle="Quantidade de vendas no período"
                                />
                            </Col>
                            <Col xl="3" lg="6" md="6">
                                <StatCard
                                    label="Total vendido"
                                    value={formatCurrency(resumo.totalValorVendido)}
                                    icon="fa fa-coins"
                                    color="#2dce89"
                                    subtitle="Soma dos valores negociados"
                                />
                            </Col>
                            <Col xl="3" lg="6" md="6">
                                <StatCard
                                    label="Comissão estimada"
                                    value={formatCurrency(resumo.totalComissaoEstimado)}
                                    icon="fa fa-percent"
                                    color="#fb6340"
                                    subtitle="Mock com taxa padrão de 8%"
                                />
                            </Col>
                            <Col xl="3" lg="6" md="6">
                                <StatCard
                                    label="Ticket médio"
                                    value={formatCurrency(resumo.ticketMedio)}
                                    icon="fa fa-chart-line"
                                    color="#11cdef"
                                    subtitle="Valor médio por venda"
                                />
                            </Col>
                        </Row>

                        <Row className="g-3 mb-4">
                            <Col xl="4" md="6">
                                <StatCard
                                    label="Clientes únicos"
                                    value={resumo.totalClientes}
                                    icon="fa fa-users"
                                    color="#8965e0"
                                    subtitle="Clientes diferentes no período"
                                />
                            </Col>
                            <Col xl="4" md="6">
                                <StatCard
                                    label="Vendas do período"
                                    value={resumo.vendasDoPeriodo}
                                    icon="fa fa-calendar-check"
                                    color="#f5365c"
                                    subtitle="Total filtrado pela data"
                                />
                            </Col>
                            <Col xl="4" md="12">
                                <StatCard
                                    label="Vendedoras ativas"
                                    value={resumo.vendedoras.length}
                                    icon="fa fa-user-tie"
                                    color="#172b4d"
                                    subtitle="Usuárias com vendas no filtro"
                                />
                            </Col>
                        </Row>

                        <Card className="shadow border-0 mb-4">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h5 className="mb-1">Ranking por vendedora</h5>
                                        <small className="text-muted">Quantidade, valor e comissão estimada</small>
                                    </div>
                                    <Badge color="info" pill>
                                        Comissão mock
                                    </Badge>
                                </div>
                                <div className="table-responsive">
                                    <Table hover className="align-items-center table-flush">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>Vendedora</th>
                                                <th>Qtd. vendas</th>
                                                <th>Total vendido</th>
                                                <th>Comissão estimada</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resumo.vendedoras.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4} className="text-center text-muted py-4">
                                                        Nenhum dado encontrado para o período selecionado.
                                                    </td>
                                                </tr>
                                            ) : (
                                                resumo.vendedoras.map(v => (
                                                    <tr key={v.vendedorId}>
                                                        <td>{v.vendedorNome}</td>
                                                        <td>{v.quantidadeVendas}</td>
                                                        <td>{formatCurrency(v.totalVendido)}</td>
                                                        <td>{formatCurrency(v.totalComissaoEstimado)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>

                        <Card className="shadow border-0 mb-4">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h5 className="mb-1">Metas das vendedoras</h5>
                                        <small className="text-muted">Gestão disponível para o Master Vendas</small>
                                    </div>
                                    <Badge color="primary" pill>Metas dinâmicas</Badge>
                                </div>

                                <Form onSubmit={handleSaveMeta} className="mb-4">
                                    <Row>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label>Vendedora</Label>
                                                <Input type="select" name="vendedorId" value={metaForm.vendedorId} onChange={handleMetaChange} required>
                                                    <option value={0}>Selecione</option>
                                                    {vendedores.map(v => (
                                                        <option key={v.id} value={v.id}>{v.name}</option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup>
                                                <Label>Início</Label>
                                                <Input type="date" name="periodoInicio" value={metaForm.periodoInicio} onChange={handleMetaChange} required />
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup>
                                                <Label>Fim</Label>
                                                <Input type="date" name="periodoFim" value={metaForm.periodoFim} onChange={handleMetaChange} required />
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup>
                                                <Label>Meta R$</Label>
                                                <Input type="number" name="metaValor" value={metaForm.metaValor} onChange={handleMetaChange} required />
                                            </FormGroup>
                                        </Col>
                                        <Col md="2" className="d-flex align-items-end">
                                            <Button color="primary" type="submit" block>
                                                Salvar meta
                                            </Button>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md="2">
                                            <FormGroup>
                                                <Label>Qtd. Meta</Label>
                                                <Input type="number" name="metaQuantidade" value={metaForm.metaQuantidade} onChange={handleMetaChange} />
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup>
                                                <Label>Meta Comissão</Label>
                                                <Input type="number" name="metaComissao" value={metaForm.metaComissao} onChange={handleMetaChange} />
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup>
                                                <Label>Ativa</Label>
                                                <Input type="select" name="ativa" value={metaForm.ativa ? "true" : "false"} onChange={handleMetaChange}>
                                                    <option value="true">Sim</option>
                                                    <option value="false">Não</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Form>

                                <div className="table-responsive">
                                    <Table hover className="align-items-center table-flush">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>Vendedora</th>
                                                <th>Período</th>
                                                <th>Meta</th>
                                                <th>Vendido</th>
                                                <th>% Atingido</th>
                                                <th>Saldo</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metaLoading ? (
                                                <tr><td colSpan={7}>Carregando metas...</td></tr>
                                            ) : metas.length === 0 ? (
                                                <tr><td colSpan={7} className="text-muted text-center py-4">Nenhuma meta cadastrada.</td></tr>
                                            ) : metas.map(meta => (
                                                <tr key={meta.id}>
                                                    <td>{meta.vendedorNome}</td>
                                                    <td>{formatDate(meta.periodoInicio)} até {formatDate(meta.periodoFim)}</td>
                                                    <td>{formatCurrency(meta.metaValor)}</td>
                                                    <td>{formatCurrency(meta.valorVendido ?? 0)}</td>
                                                    <td>{Number(meta.percentualAtingido ?? 0).toFixed(1)}%</td>
                                                    <td>{formatCurrency(meta.saldoMeta ?? 0)}</td>
                                                    <td><Badge color={meta.ativa ? "success" : "secondary"}>{meta.ativa ? "Ativa" : "Inativa"}</Badge></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>

                        <Card className="shadow border-0">
                            <CardBody>
                                <h5 className="mb-3">Últimas vendas</h5>
                                <div className="table-responsive">
                                    <Table hover className="align-items-center table-flush">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>Venda</th>
                                                <th>Data</th>
                                                <th>Cliente</th>
                                                <th>Produto</th>
                                                <th>Vendedora</th>
                                                <th>Valor</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resumo.ultimasVendas.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted py-4">
                                                        Nenhuma venda encontrada.
                                                    </td>
                                                </tr>
                                            ) : (
                                                resumo.ultimasVendas.map(v => (
                                                    <tr key={v.vendaId}>
                                                        <td>
                                                            <Badge color="primary" pill>#{v.vendaId}</Badge>
                                                        </td>
                                                        <td>{formatDate(v.data)}</td>
                                                        <td>{v.clienteNome}</td>
                                                        <td>{v.produtoNome}</td>
                                                        <td>{v.vendedoraNome}</td>
                                                        <td>{formatCurrency(v.valor)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </>
                )}
            </Container>
        </>
    );
};

export default VendasDashboard;
